import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAvatarUrl, DEFAULT_AVATAR_ID } from '../../config/avatars';
import { SidebarInner, BrandMark } from '../dashboard/AppSidebar';
import {
  LayoutDashboard, Building2, Users, Briefcase, UserSearch, MessagesSquare,
  CheckSquare, Award, Euro, BarChart3, Settings, Menu, X, Building, Rocket,
} from 'lucide-react';

const NAV_ITEMS = [
  { path: '/sales-dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/accounts', label: 'Accounts', icon: Building2 },
  { path: '/crm-contacts', label: 'Contacts', icon: Users },
  { path: '/crm-jobs', label: 'Jobs', icon: Briefcase },
  { path: '/crm-candidates', label: 'Candidates', icon: UserSearch },
  { path: '/crm-activities', label: 'Activities', icon: MessagesSquare },
  { path: '/sales-tasks', label: 'Tasks', icon: CheckSquare },
  { path: '/crm-placements', label: 'Placements', icon: Award },
  { path: '/crm-commission', label: 'Commission', icon: Euro },
  { path: '/software-pipeline', label: 'Software', icon: Rocket },
  { path: '/sales-companies', label: 'Companies', icon: Building },
  { path: '/sales-reports', label: 'Reports', icon: BarChart3 },
  { path: '/sales-settings', label: 'Settings', icon: Settings },
];

const readCollapsed = () => {
  try { return localStorage.getItem('iq_sidebar_collapsed') === '1'; } catch { return false; }
};

export const SalesLayout = ({ children }) => {
  const { userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);

  const toggleCollapse = () => setCollapsed((c) => {
    const next = !c;
    try { localStorage.setItem('iq_sidebar_collapsed', next ? '1' : '0'); } catch { /* noop */ }
    return next;
  });

  const handleLogout = () => { signOut(); navigate('/login'); };

  const displayName = userProfile?.firstName && userProfile?.lastName
    ? `${userProfile.firstName} ${userProfile.lastName}`
    : userProfile?.email || '';
  const avatarUrl = getAvatarUrl(userProfile?.avatarUrl || DEFAULT_AVATAR_ID);
  const isAdmin = userProfile?.role === 'jobasti_admin';

  const sidebarProps = {
    navItems: NAV_ITEMS,
    brand: { homePath: '/sales-dashboard', tagline: 'Sales CRM' },
    user: { displayName, roleLabel: isAdmin ? 'Admin (CRM view)' : 'Sales Executive', avatarUrl },
    onLogout: handleLogout,
    onProfile: () => { setMobileOpen(false); navigate('/sales-settings'); },
    ids: {
      brand: 'crm-brand',
      tagline: 'crm-tenant-badge',
      nav: 'crm-nav',
      navPrefix: 'crm-nav',
      profile: 'crm-menu-profile',
      logout: 'crm-logout-btn',
      collapse: 'crm-collapse-toggle',
    },
  };

  return (
    <div className="min-h-screen app-shell">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex lg:flex-col fixed inset-y-0 left-0 z-40 transition-all duration-300 ${collapsed ? 'w-20' : 'w-64'}`}
        data-testid="crm-sidebar"
      >
        <SidebarInner {...sidebarProps} collapsed={collapsed} onToggleCollapse={toggleCollapse} />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14">
        <Link to="/sales-dashboard" className="flex items-center gap-2.5">
          <BrandMark size={30} />
          <h1 className="text-lg font-display font-bold text-slate-900 notranslate" translate="no">Jobasti<span className="text-blue-600">IQ</span></h1>
        </Link>
        <button onClick={() => setMobileOpen(true)} className="text-slate-700" data-testid="crm-mobile-menu-btn">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="crm-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 flex flex-col shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-3 z-10 text-slate-400 hover:text-slate-700"
              data-testid="crm-mobile-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarInner {...sidebarProps} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className={`${collapsed ? 'lg:pl-20' : 'lg:pl-64'} transition-all duration-300`}>
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>
      </div>
    </div>
  );
};

export default SalesLayout;
